// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { hexTorgba } from '../../utils/style';
import defaultTheme from './default';

const colors = {
  primary: {
    lightest: '#88b2ff',
    lighter: '#f3f6f8',
    light: '#f0f3f6',
    main: '#275ea7',
    dark: '#727fb9',
    darker: '#0042bb',
    darkest: '#002f85'
  },

  secondary: {
    light: '#ff8e74',
    main: '#ff7654',
    dark: '#e86c4d'
  },

  error: {
    lightest: '#FCF7F6',
    lighter: '#ffe4e7',
    light: '#ff184e',
    primary: '#ca0a37',
    dark: '#9E3319',
    darker: '#89301A',
    darkest: '#791800'
  },

  success: {
    lightest: '#EBF1EA',
    lighter: '#CEE0C8',
    light: '#50CD49',
    primary: '#067000',
    dark: '#305D1D',
    darker: '#2C511D',
    darkest: '#184206'
  },

  info: {
    lightest: '#DADFE7',
    lighter: '#C4CCD9',
    light: '#418AFD',
    primary: '#e6eaff',
    dark: '#c1c8e2',
    darker: '#243F6B',
    darkest: '#123366'
  },

  warning: {
    lightest: '#FAF8EA',
    lighter: '#F7E79E',
    light: '#fffbcd',
    primary: '#F9CC09',
    dark: '#665A2A',
    darker: '#584E26',
    darkest: '#534201'
  },

  highlight: {
    main: '#eb5b24'
  },

  greys: {
    black: '#000000',
    grey100: '#282828',
    grey90: '#2d2d2d',
    grey80: '#333333',
    grey70: '#4d4d4d',
    grey60: '#666666',
    grey50: '#7b7b7b',
    grey40: '#999999',
    grey30: '#b2b2b2',
    grey20: '#cccccc',
    grey10: '#f0f0f0',
    white: '#ffffff'
  }
};

const globalStyle = {
  bgd: colors.primary.light,
  stripedBgd: `repeating-linear-gradient(135deg, transparent, transparent 25px, ${colors.primary.lighter} 25px, ${colors.primary.lighter} 43px)`,
  text: colors.greys.grey80,
  fontSize: defaultTheme.fontSizes.baseFontSize
};

const shadows = {
  none: 'none',
  small: `0 0.125rem 0.375rem 0 ${hexTorgba(colors.primary.dark, 0.1)}`,
  medium: `0 0.25rem 0.25rem 0 ${hexTorgba(colors.primary.dark, 0.16)}`,
  large: `0 0.75rem 1.875rem 0 ${hexTorgba(colors.greys.black, 0.15)}`
};

const buttons = {
  primary: {
    shadow: shadows.none,
    static: {
      bgd: colors.primary.main,
      border: 'none',
      text: colors.greys.white,
      shadow: shadows.medium
    },
    outline: {
      bgd: colors.greys.white,
      border: colors.primary.main,
      text: colors.primary.main,
      shadow: 'none'
    },
    disabled: {
      bgd: colors.greys.grey20,
      border: 'none',
      text: colors.greys.white,
      shadow: shadows.medium
    }
  },

  secondary: {
    shadow: shadows.none,
    static: {
      bgd: colors.greys.grey10,
      border: 'none',
      text: colors.greys.grey80,
      shadow: shadows.medium
    },
    disabled: {
      bgd: colors.greys.white,
      border: `0.03125rem solid ${colors.greys.grey10}`,
      text: colors.greys.grey40,
      shadow: 'none'
    }
  },

  info: {
    shadow: shadows.none,
    static: {
      bgd: colors.info.primary,
      border: 'none',
      text: colors.primary.main,
      shadow: shadows.none
    },
    outline: {
      bgd: colors.greys.white,
      border: colors.primary.main,
      text: colors.primary.main,
      shadow: 'none'
    },
    disabled: {
      bgd: colors.greys.grey20,
      text: colors.greys.white,
      shadow: 'none'
    }
  },

  error: {
    shadow: shadows.none,
    static: {
      bgd: colors.error.primary,
      border: 'none',
      text: colors.greys.white,
      shadow: shadows.medium
    },
    outline: {
      bgd: colors.greys.white,
      border: colors.error.primary,
      text: colors.error.primary,
      shadow: 'none'
    },
    disabled: {
      bgd: colors.greys.white,
      text: colors.greys.grey40,
      shadow: 'none'
    }
  },

  icon: {
    shadow: `0 0.1875rem 0.375rem 0 ${hexTorgba(colors.primary.dark, 0.4)}`,
    static: {
      bgd: 'transparent',
      border: '0.03125rem  solid transparent',
      text: colors.greys.grey80,
      shadow: 'none'
    },
    hover: {
      bgd: colors.primary.dark,
      border: '0.03125rem  solid transparent',
      text: colors.greys.white,
      shadow: 'none'
    },
    focus: {
      bgd: 'transparent',
      border: `0.03125rem  solid ${colors.primary.darker}`,
      text: colors.greys.grey80,
      shadow: `0 0 0 0.25rem ${colors.primary.lightest}`
    },
    active: {
      bgd: colors.primary.darker,
      border: `0.03125rem  solid ${colors.primary.darker}`,
      text: colors.greys.white,
      shadow: `0 0 0 0.25rem ${colors.primary.lightest}`
    },
    selected: {
      bgd: colors.primary.light,
      border: `0.03125rem  solid ${colors.primary.darker}`,
      text: colors.greys.white,
      shadow: 'none'
    },
    disabled: {
      bgd: 'transparent',
      border: 'none',
      text: colors.greys.grey40,
      shadow: 'none'
    }
  }
};

const inputs = {
  bgd: colors.greys.white,
  border: `0.0625rem solid ${colors.greys.grey20}`,
  borderRadius: defaultTheme.radii.small,
  fontColor: colors.greys.grey80,
  labelColor: colors.greys.grey50,
  placeholder: colors.greys.grey20,
  clearBg: colors.greys.grey60,

  error: {
    border: `0.0625rem solid ${colors.error.dark}`,
    fontColor: colors.error.primary,
    shadow: `0 0 0 0.125rem ${colors.error.light}`
  },

  checked: {
    bgd: colors.primary.main,
    border: `solid 0.0625rem ${colors.primary.main}`,
    fontColor: colors.greys.white
  },

  disabled: {
    bgd: colors.greys.grey10,
    fontColor: colors.greys.grey50
  }
};

const modal = {
  bgd: colors.greys.white,
  text: colors.greys.grey80,
  wrapperBgd: hexTorgba(colors.greys.grey80, 0.7),
  titleSize: defaultTheme.fontSizes.h5.fontSize,
  titleWeight: 'normal',
  shadow: shadows.large,
  border: colors.greys.grey30
};

const popOver = {
  menuBgd: colors.greys.white,
  menuBorder: `1px solid ${colors.greys.grey20}`,
  shadow: 'none',
  itemBgd: 'transparent',
  itemText: colors.greys.grey80,
  titleText: colors.greys.grey80,
  active: {
    itemBgd: colors.primary.light,
    itemText: colors.greys.grey80
  },
  disabled: colors.greys.grey40,
  separator: `${hexTorgba(colors.greys.grey40, 0.3)}`,
  tooltipBorder: `1px solid ${colors.primary.main}`,
  tooltipText: colors.primary.main
};

const notification = {
  shadow: shadows.large,
  error: {
    text: colors.error.lightest,
    closeButton: {
      text: colors.error.lighter,
      hover: {
        bgd: colors.error.lighter,
        text: colors.error.dark
      },
      active: {
        bgd: colors.error.lightest,
        text: colors.error.darker
      }
    }
  },

  success: {
    text: colors.success.lightest,
    closeButton: {
      text: colors.success.lighter,
      hover: {
        bgd: colors.success.lighter,
        text: colors.success.dark
      },
      active: {
        bgd: colors.success.lightest,
        text: colors.success.darker
      }
    }
  },

  info: {
    text: colors.info.lightest,
    closeButton: {
      text: colors.info.lighter,
      hover: {
        bgd: colors.info.lighter,
        text: colors.info.dark
      },
      active: {
        bgd: colors.info.lightest,
        text: colors.info.darker
      }
    }
  },

  warning: {
    text: colors.warning.darker,
    closeButton: {
      text: colors.warning.dark,
      hover: {
        bgd: colors.warning.dark,
        text: colors.greys.white
      },
      active: {
        bgd: colors.warning.darker,
        text: colors.greys.white
      }
    }
  }
};

const links = {
  fontColor: colors.primary.main,
  fontColorHover: colors.primary.dark,
  fontColorActive: colors.primary.darker,
  fontColorVisited: colors.primary.darkest
};

const controlBar = {
  text: colors.greys.grey70,
  shadow: shadows.large,
  bgd: colors.greys.white,
  border: `0.03125rem solid ${colors.greys.grey20}`,
  opacity: 1,
  selected: {
    text: colors.greys.white,
    bgd: colors.primary.light
  }
};

const roster = {
  title: colors.greys.grey100,
  primaryText: colors.greys.grey80,
  secondaryText: colors.greys.grey50,
  headerBorder: colors.greys.grey40,
  containerBorder: colors.greys.grey30,
  bgd: colors.greys.grey10,
  fgd: colors.greys.white,
  shadow: shadows.large,
  maxWidth: '18.5rem'
};

const navbar = {
  text: colors.greys.grey80,
  bgd: colors.greys.grey10,
  headerBorder: colors.greys.grey40,
  wrapperBgd: hexTorgba(colors.greys.grey50, 0.9)
};

const videoGrid = {
  bgd: colors.greys.white
};

const chatBubble = {
  boxShadow: `0 0.1875rem 0.375rem 0 ${hexTorgba(colors.primary.dark, 0.2)}`,
  highlight: {
    bgd: colors.warning.light,
    border: `1px solid ${colors.warning.light}`,
    fontColor: colors.greys.grey80
  },
  editable: {
    bgd: colors.greys.white,
    border: `1px solid ${colors.primary.main}`,
    fontColor: colors.greys.grey80
  },
  incoming: {
    bgd: colors.greys.white,
    borderRadius: '0 1rem 1rem 1rem',
    fontColor: colors.greys.grey80,
    linkColor: colors.primary.main,
    linkColorHover: colors.primary.dark,
    linkColorActive: colors.primary.darker,
    linkColorVisited: colors.primary.darkest
  },
  outgoing: {
    bgd: colors.primary.main,
    borderRadius: '1rem 0 1rem 1rem',
    fontColor: colors.greys.white,
    linkColor: colors.greys.white,
    linkColorHover: colors.greys.grey10,
    linkColorActive: colors.greys.grey20,
    linkColorVisited: colors.greys.grey30
  },
  container: {
    fontColor: colors.greys.grey70,
    bgd: colors.greys.grey10
  }
};

const messageAttachment = {
  size: {
    fontColor: colors.greys.grey40,
    bgd: colors.greys.white,
    letterSpacing: '-0.07px',
    lineHight: '16px',
    fontSize: '10.4px'
  },
  icon: {
    bgd: colors.greys.grey10,
    color: colors.greys.grey80
  },
  name: {
    fontColor: colors.greys.grey80
  },
  content: {
    letterSpacing: '-0.09px',
    bgd: colors.greys.white,
    fontColor: colors.greys.grey80
  }
};

const channelList = {
  bgd: colors.greys.white,
  fontColor: colors.greys.grey80,
  timeFontColor: colors.greys.grey20,
  hover: {
    bgd: colors.primary.light
  },
  selected: {
    bgd: colors.primary.light
  },
  iconButton: {
    activeBgd: colors.greys.white
  }
};

const chatDateHeader = {
  bgd: colors.info.dark,
  fontColor: colors.greys.white
};

const loading = {
  black: {
    bgd: modal.wrapperBgd,
    iconColor: colors.greys.white
  },
  white: {
    bgd: hexTorgba(colors.greys.white, 0.7),
    iconColor: hexTorgba(colors.greys.grey80, 0.7)
  }
};

export const lightTheme = {
  name: 'Light Theme',
  buttons,
  colors,
  globalStyle,
  links,
  shadows,
  inputs,
  modal,
  popOver,
  notification,
  controlBar,
  roster,
  navbar,
  videoGrid,
  chatBubble,
  channelList,
  chatDateHeader,
  messageAttachment,
  loading,
  ...defaultTheme
};

export default lightTheme;
